import React, { useState, useEffect } from 'react';
import {
  Card,
  Transfer,
  Select,
  Button,
  Space,
  Typography,
  message,
  Spin,
  Checkbox,
} from 'antd';
import {
  UserOutlined,
  ArrowRightOutlined,
  ArrowLeftOutlined,
} from '@ant-design/icons';
import { UserService, User } from '../../services/api/userService';
import styles from './UserCompanyAssignment.module.css';

const { Title, Text } = Typography;
const { Option } = Select;

interface UserCompanyAssignmentProps {
  companyId: string;
  onAssignmentChange?: () => void;
}

interface TransferItem {
  key: string;
  title: string;
  description?: string;
}

export const UserCompanyAssignment: React.FC<UserCompanyAssignmentProps> = ({
  companyId,
  onAssignmentChange,
}) => {
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [assignedUsers, setAssignedUsers] = useState<User[]>([]);
  const [targetKeys, setTargetKeys] = useState<string[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [userRoles, setUserRoles] = useState<Record<string, string>>({});

  useEffect(() => {
    loadUsers();
  }, [companyId]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      // Get all users
      const users = await UserService.getAll();
      setAllUsers(users);

      // Get users assigned to this company
      const companyUsers = await UserService.getCompanies(companyId);
      const assigned = users.filter(user =>
        companyUsers.some((cu: any) => cu.user_id === user.id || cu.id === user.id)
      );
      setAssignedUsers(assigned);
      setTargetKeys(assigned.map(u => u.id));

      // Load roles for assigned users
      const roles: Record<string, string> = {};
      for (const user of assigned) {
        const userCompanies = await UserService.getCompanies(user.id);
        const companyAssignment = userCompanies.find((uc: any) => uc.company_id === companyId);
        if (companyAssignment?.role) {
          roles[user.id] = companyAssignment.role;
        }
      }
      setUserRoles(roles);
    } catch (error) {
      console.error('Error loading users:', error);
      message.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (newTargetKeys: React.Key[]) => {
    const newKeys = newTargetKeys.map(key => String(key));
    const previousKeys = [...targetKeys];
    setTargetKeys(newKeys);

    // Determine which users were added/removed
    const added = newKeys.filter(key => !previousKeys.includes(key));
    const removed = previousKeys.filter(key => !newKeys.includes(key));

    // Perform async operations
    (async () => {
      try {
        // Add users to company
        for (const userId of added) {
          await UserService.addToCompany(userId, companyId, 'member', true);
        }

        // Remove users from company
        for (const userId of removed) {
          await UserService.removeFromCompany(userId, companyId);
        }

        message.success('User assignments updated successfully');
        if (onAssignmentChange) {
          onAssignmentChange();
        }
        await loadUsers();
      } catch (error) {
        console.error('Error updating user assignments:', error);
        message.error('Failed to update user assignments');
        // Revert on error
        setTargetKeys(previousKeys);
      }
    })();
  };

  const handleRoleChange = async (userId: string, role: string) => {
    try {
      // Remove and re-add with new role
      await UserService.removeFromCompany(userId, companyId);
      await UserService.addToCompany(userId, companyId, role, true);
      setUserRoles(prev => ({ ...prev, [userId]: role }));
      message.success('User role updated successfully');
      if (onAssignmentChange) {
        onAssignmentChange();
      }
    } catch (error) {
      console.error('Error updating user role:', error);
      message.error('Failed to update user role');
    }
  };

  const filterOption = (inputValue: string, option: TransferItem) => {
    return option.title.toLowerCase().indexOf(inputValue.toLowerCase()) > -1;
  };

  const renderItem = (item: TransferItem) => {
    return {
      label: (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <span>{item.title}</span>
          {targetKeys.includes(item.key) && (
            <Select
              size="small"
              style={{ width: 120 }}
              value={userRoles[item.key] || 'member'}
              onChange={(value) => handleRoleChange(item.key, value)}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <Option value="member">Member</Option>
              <Option value="admin">Admin</Option>
              <Option value="owner">Owner</Option>
              <Option value="company_admin">Company admin</Option>
            </Select>
          )}
        </div>
      ),
      value: item.title,
    };
  };

  const transferData: TransferItem[] = allUsers.map(user => ({
    key: user.id,
    title: user.username || user.email,
    description: user.email,
  }));

  return (
    <div className={styles.userCompanyAssignment}>
      <Spin spinning={loading}>
        <Card>
        <Title level={5}>Assign Users to Company</Title>
        <Text type="secondary" style={{ marginBottom: '16px', display: 'block' }}>
          Select users from the left to assign them to this company. Use the role selector to set permissions.
        </Text>
        <Transfer
          dataSource={transferData}
          titles={['Available Users', 'Assigned Users']}
          targetKeys={targetKeys}
          selectedKeys={selectedKeys}
          onChange={handleChange}
          onSelectChange={(sourceSelectedKeys, targetSelectedKeys) => {
            setSelectedKeys([
              ...sourceSelectedKeys.map(k => String(k)),
              ...targetSelectedKeys.map(k => String(k))
            ]);
          }}
          render={renderItem}
          filterOption={filterOption}
          showSearch
          listStyle={{
            width: '45%',
            height: 400,
          }}
        />
        </Card>
      </Spin>
    </div>
  );
};
